// ============================================================================
// patientCabinet.ts — «Кабинет пациента» (psy, КЛИЕНТСКИЙ срез). Данные — только
// существующими RPC ядра (новых серверных механизмов нет):
//   • доступные методики — PollService.ListMyAssignments (назначение по ролям,
//     пусто = всем) + ListPollTypes (резолв ШАБЛОН → ТИП опроса по template_id,
//     именно код типа — физическая таблица протоколов);
//   • свои результаты — MetadataService.ListTableData по типу опроса с отбором
//     owner_user_id = текущий пользователь (протоколы персональны: реквизит
//     data_type='owner'); сервер дополнительно изолирует записи (OwnScope);
//   • связь с клиентом — справочник psy_clients по реквизиту user_id (карточка,
//     созданная специалистом при первом сохранении, содержит привязку пациента).
// Права объектов — SSoT СЕРВЕРА: если роли «Пациент» не выдано право «Чтение»
// на тип опроса/справочник, выдача помечается denied — кабинет показывает это
// текстом (не пустой таблицей и не «вечной загрузкой»).
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import type { Poll, PollTypeInfo, TableDataRow } from '@zdc/sdk';
import { PSY_CLIENTS_CATALOG, hasPsyClientsCatalog } from './polls';
import { isNotFoundError, isPermissionDeniedError, logSwallowed } from './errors';

/** Системная роль пациента (имя = код, создаётся ядром: auth.EnsurePatientRole). */
export const PATIENT_ROLE = 'patient';
/** Код приложения «Мой кабинет» (рендерер регистрируется клиентом). */
export const PATIENT_CABINET_APP_CODE = 'patient_cabinet';

/** Доступная пациенту методика (тип опроса + привязанный шаблон вопросов). */
export interface PatientTest {
    /** Код ТИПА опроса — физическая таблица протоколов (ListTableData). */
    code: string;
    /** id шаблона вопросов — SubmitPollAnswers.poll_id (GetPollFull). */
    templateId: string;
    name: string;
    synonym: string;
    description: string;
}

/** Свой протокол прохождения (строка персонального типа опроса). */
export interface PatientProtocol {
    id: string;
    testCode: string;
    testName: string;
    createdAt: string;
    score: string;
    passed: boolean;
    conclusion: string;
}

/** Карточка клиента psy, связанная с учётной записью пациента. */
export interface PatientCard {
    id: string;
    name: string;
    phone: string;
    email: string;
}

/** Данные кабинета: тесты, свои результаты, карточка клиента (+ доступность). */
export interface PatientCabinetData {
    tests: PatientTest[];
    protocols: PatientProtocol[];
    /** Право чтения протоколов роли «Пациент» не выдано (SSoT — сервер). */
    protocolsDenied: boolean;
    card: PatientCard | null;
    /** Право чтения карточки клиента роли «Пациент» не выдано. */
    cardDenied: boolean;
    /** К учётной записи привязано несколько карточек — выбирать одну нельзя. */
    cardAmbiguous: boolean;
}

/** Протоколы — персональные строки: отбор владельца (сервер изолирует тем же). */
const OWNER_FILTER_COLUMN = 'owner_user_id';

function cellsOf(row: TableDataRow): Map<string, string> {
    const cells = new Map<string, string>();
    for (const v of row.values) cells.set(String(v.column), String(v.value ?? ''));
    return cells;
}

function testLabel(t: PollTypeInfo): string {
    return String(t.synonym || t.name || t.code);
}

function isTrue(v: string): boolean {
    const s = v.trim().toLowerCase();
    return s === 'true' || s === '1' || s === 'да';
}

/**
 * mapTests — доступные методики: назначенные пользователю шаблоны (по ролям;
 * пустой список ролей = назначено всем), спроецированные на ТИП опроса по
 * template_id. Шаблон без типа пропускается: протоколов у него нет.
 */
export function mapTests(types: PollTypeInfo[], assignments: Poll[]): PatientTest[] {
    const typeByTemplate = new Map<string, PollTypeInfo>();
    for (const t of types) {
        if (t.templateId) typeByTemplate.set(t.templateId, t);
    }
    const out: PatientTest[] = [];
    const seen = new Set<string>();
    for (const a of assignments) {
        const t = typeByTemplate.get(a.id);
        if (!t || seen.has(t.code)) continue;
        seen.add(t.code);
        out.push({
            code: t.code,
            templateId: t.templateId,
            name: t.name,
            synonym: testLabel(t),
            description: String(a.description || ''),
        });
    }
    out.sort((x, y) => x.synonym.localeCompare(y.synonym, 'ru'));
    return out;
}

/**
 * loadMyProtocols — собственные протоколы по ВСЕМ типам опросов базы (включая
 * закрытые: история результатов не должна исчезать). Первый отказ в праве
 * чтения прекращает чтение — кабинет честно сообщает об ограничении доступа.
 */
export async function loadMyProtocols(types: PollTypeInfo[], userId: string): Promise<{ items: PatientProtocol[]; denied: boolean }> {
    const items: PatientProtocol[] = [];
    for (const t of types) {
        try {
            const res = await getSDK().metadata.listTableData({
                schema: 'public',
                table: t.code,
                pageSize: 200,
                page: 0,
                filters: [{ column: OWNER_FILTER_COLUMN, value: userId }],
            });
            for (const row of res.rows) {
                const cells = cellsOf(row);
                items.push({
                    id: row.id,
                    testCode: t.code,
                    testName: testLabel(t),
                    createdAt: cells.get('created_at') || '',
                    score: cells.get('score') || '',
                    passed: isTrue(cells.get('passed') || ''),
                    conclusion: cells.get('conclusion') || '',
                });
            }
        } catch (e) {
            if (isPermissionDeniedError(e)) return { items, denied: true };
            if (isNotFoundError(e)) {
                // Тип без физической таблицы (метаданные без DDL) — пропускаем.
                logSwallowed(e, `patientCabinet: протоколы типа ${t.code}`);
                continue;
            }
            throw e;
        }
    }
    items.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return { items, denied: false };
}

/** loadMyCard — карточка клиента psy по реквизиту user_id (если справочник есть). */
async function loadMyCard(userId: string): Promise<{ card: PatientCard | null; denied: boolean; ambiguous: boolean }> {
    let isPsy: boolean;
    try {
        isPsy = await hasPsyClientsCatalog();
    } catch (e) {
        logSwallowed(e, 'patientCabinet: проверка справочника psy_clients');
        return { card: null, denied: false, ambiguous: false };
    }
    if (!isPsy) return { card: null, denied: false, ambiguous: false };
    try {
        const res = await getSDK().metadata.listTableData({
            schema: 'public',
            table: PSY_CLIENTS_CATALOG,
            pageSize: 2,
            page: 0,
            filters: [{ column: 'user_id', value: userId }],
        });
        // Несколько карточек на одну учётную запись — НЕ выбираем первую:
        // это медицинские данные, кабинет просит уточнить у специалиста.
        if (res.rows.length > 1) return { card: null, denied: false, ambiguous: true };
        const row = res.rows[0];
        if (!row) return { card: null, denied: false, ambiguous: false };
        const cells = cellsOf(row);
        return {
            card: {
                id: row.id,
                name: cells.get('name') || '',
                phone: cells.get('phone') || '',
                email: cells.get('email') || '',
            },
            denied: false,
            ambiguous: false,
        };
    } catch (e) {
        if (isPermissionDeniedError(e)) return { card: null, denied: true, ambiguous: false };
        logSwallowed(e, 'patientCabinet: карточка клиента');
        return { card: null, denied: false, ambiguous: false };
    }
}

/**
 * loadPatientCabinet — единая загрузка кабинета (SRP: страница только рендерит).
 * Ошибки сети/сервера пробрасываются (кабинет показывает их с «Повторить»);
 * отказ в правах — флаг denied, а не ошибка.
 */
export async function loadPatientCabinet(userId: string): Promise<PatientCabinetData> {
    const sdk = getSDK();
    const [typesRes, assignedRes] = await Promise.all([
        sdk.poll.listPollTypes({}),
        sdk.poll.listMyAssignments({}),
    ]);
    const types = typesRes.items;
    const tests = mapTests(types, assignedRes.items);
    const protocols = await loadMyProtocols(types, userId);
    const card = await loadMyCard(userId);
    return {
        tests,
        protocols: protocols.items,
        protocolsDenied: protocols.denied,
        card: card.card,
        cardDenied: card.denied,
        cardAmbiguous: card.ambiguous,
    };
}

/** Тексты заключений из SubmitPollAnswers (conclusions_json) — для показа итога. */
export function conclusionsOf(json: string): string[] {
    try {
        const parsed: unknown = JSON.parse(json || '{}');
        if (!parsed || typeof parsed !== 'object') return [];
        return Object.values(parsed as Record<string, unknown>)
            .map((v) => String(v ?? '').trim())
            .filter((s) => s !== '');
    } catch (e) {
        logSwallowed(e, 'patientCabinet: разбор заключения');
        return [];
    }
}
